import {
  BillGroupSummarySchema,
  ContractRespSchema,
} from '@monetize/types/app';
import {
  ApiListResponse,
  FilterType,
  TDataTablePager,
} from '@monetize/types/common';
import { arrayToObject } from '@monetize/utils/core';
import { useEffect, useMemo, useState } from 'react';
import { useAtom } from 'jotai';
import { z } from 'zod';
import { useGetAllBillGroups } from '../../api/billGroupService';
import { useGetContracts } from '../../api/cpqService';
import {
  tableFilterSelector,
  tablePagerSelector,
} from '../../store/global.store';
import {
  FilterStateKeys,
  getDefaultFilter,
  getDefaultPager,
} from '../../store/store.types';
import { buildFilterParamsRequestObject } from '../../utils';

const ContractWithBillGroup = ContractRespSchema.extend({
  billGroup: BillGroupSummarySchema.nullish(),
});

export type IContractWithBillGroup = z.infer<typeof ContractWithBillGroup>;

export const useContractsData = (
  accountId: string,
  billGroupId?: string,
  filterKey: string = FilterStateKeys.CONTRACT_LIST,
  { enabled = true }: { enabled?: boolean } = {},
) => {
  const [initialFilters, persistTableFilter] = useAtom(
    tableFilterSelector(filterKey),
  );
  const [persistedPager, setPersistedPager] = useAtom(
    tablePagerSelector(filterKey),
  );

  const [filters, setFilters] = useState<FilterType[]>(initialFilters);
  const [searchKey] = useState<string>('description');
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [pager, setPager] = useState<TDataTablePager>(persistedPager);
  const filterParams = useMemo(
    () => buildFilterParamsRequestObject(filters, searchTerm, searchKey),
    [filters, searchTerm, searchKey],
  );

  useEffect(() => {
    persistTableFilter(filters);
  }, [filters, persistTableFilter]);

  useEffect(() => {
    setPersistedPager(pager);
  }, [pager, setPersistedPager]);

  const [contractsWithBillGroup, setContractsWithBillGroup] =
    useState<ApiListResponse<IContractWithBillGroup>>();

  const {
    isLoading: isBillGroupResLoading,
    isFetching: isBillGroupResFetching,
    data: billGroupsById,
  } = useGetAllBillGroups(
    { filters: { accountId } },
    {
      enabled: !!accountId,
      select: (data) => arrayToObject(data, 'id'),
    },
  );

  const {
    isLoading,
    isFetched,
    isRefetching,
    data: listData,
    isError,
    error,
    refetch,
  } = useGetContracts(
    {
      config: pager,
      filters: billGroupId ? { ...filterParams, billGroupId } : filterParams,
      url: `/api/accounts/${accountId}/contracts`,
    },
    { enabled, meta: { showErrorToast: true } },
  );

  useEffect(() => {
    if (listData && billGroupsById) {
      setContractsWithBillGroup(() => {
        return {
          ...listData,
          content: listData.content.map((contract) => {
            return {
              ...contract,
              billGroup: contract.billGroupId
                ? billGroupsById[contract.billGroupId]
                : undefined,
            };
          }),
        };
      });
    }
  }, [listData, billGroupsById]);

  const onResetFilter = () => {
    setFilters(getDefaultFilter(filterKey));
    setPager(getDefaultPager(filterKey));
  };

  return {
    pager,
    setPager,
    filters,
    setFilters,
    onResetFilter,
    searchKey,
    searchTerm,
    setSearchTerm,
    totalRecords: listData?.totalElements || 0,
    listData: contractsWithBillGroup,
    loading: isLoading || isBillGroupResLoading || (isRefetching && !isFetched),
    isError,
    error,
    refetch,
  };
};
